import React, { useCallback, useEffect, useState } from 'react';
import particularesApiService from '../../services/ParticularesApiService';
import { formatFechaHoraUtc, formatRestante, getEstadoAsistencia, normalizeAsistencia } from './asistenciasUtils';
import './Asistencias.css';

// Asistencias del alumno particular: tomas abiertas (da el presente con el código), estado por curso e historial.

const TomaAbierta = ({ asistencia, now, onPresente }) => {
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });

  const enviar = async (event) => {
    event.preventDefault();
    if (codigo.length !== 6) {
      setMensaje({ tipo: 'error', texto: 'El código tiene 6 dígitos.' });
      return;
    }
    setEnviando(true);
    setMensaje({ tipo: '', texto: '' });
    try {
      const response = await particularesApiService.darPresente(asistencia.id, codigo);
      setMensaje({ tipo: 'ok', texto: response?.mensaje || response?.Mensaje || '¡Listo! Tu presente quedó registrado.' });
      setTimeout(() => onPresente?.(), 1200);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err?.message || 'No pudimos registrar tu presente.' });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form className="as-toma" onSubmit={enviar}>
      <div className="as-toma-head">
        <span className="as-chip as-chip-live">Abierta</span>
        <strong>{asistencia.divisionNombre}{asistencia.titulo ? ` · ${asistencia.titulo}` : ''}</strong>
        <small><i className="fas fa-hourglass-half" aria-hidden="true" /> Cierra en {formatRestante(asistencia.venceUtc, now)}</small>
      </div>
      <label className="as-toma-label" htmlFor={`codigo-${asistencia.id}`}>Ingresá el código que te dieron</label>
      <div className="as-toma-row">
        <input
          id={`codigo-${asistencia.id}`}
          className="as-codigo-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={codigo}
          onChange={(event) => setCodigo(event.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          disabled={enviando}
        />
        <button type="submit" className="as-btn as-btn-primary" disabled={enviando || codigo.length !== 6}>
          <i className={`fas ${enviando ? 'fa-spinner fa-spin' : 'fa-check'}`} aria-hidden="true" />
          Dar presente
        </button>
      </div>
      {mensaje.texto && <p className={mensaje.tipo === 'ok' ? 'as-ok' : 'as-error'} role="status">{mensaje.texto}</p>}
    </form>
  );
};

const AlumnoAsistencias = () => {
  const [data, setData] = useState({ abiertas: [], historial: [], resumenPorCurso: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const response = await particularesApiService.getAlumnoAsistencias();
      setData({
        abiertas: (response?.abiertas || response?.Abiertas || []).map(normalizeAsistencia),
        historial: (response?.historial || response?.Historial || []).map(normalizeAsistencia),
        resumenPorCurso: response?.resumenPorCurso || response?.ResumenPorCurso || []
      });
      setError('');
    } catch (err) {
      setError(err?.message || 'No pudimos cargar tus asistencias.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Con tomas abiertas: reloj cada segundo y recarga cuando vence alguna.
  useEffect(() => {
    if (data.abiertas.length === 0) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [data.abiertas.length]);

  useEffect(() => {
    if (data.abiertas.some((a) => new Date(a.venceUtc).getTime() <= now)) load();
  }, [data.abiertas, now, load]);

  const v = (item, lower, upper) => item?.[lower] ?? item?.[upper];

  return (
    <div className="as-alumno">
      <section className="as-panel">
        <header className="as-header">
          <div>
            <h2><i className="fas fa-clipboard-check" aria-hidden="true" /> Asistencias</h2>
            <p>Cuando se toma asistencia te llega un aviso: ingresá el código para dar el presente.</p>
          </div>
          <button type="button" className="as-btn" onClick={load} disabled={loading}>
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} aria-hidden="true" />
            Actualizar
          </button>
        </header>
        {error && <p className="as-error" role="alert">{error}</p>}
        {data.abiertas.length === 0 ? (
          <p className="as-empty-card">{loading ? 'Cargando...' : 'No hay tomas de asistencia abiertas ahora.'}</p>
        ) : (
          <div className="as-tomas">
            {data.abiertas.map((asistencia) => (
              <TomaAbierta key={asistencia.id} asistencia={asistencia} now={now} onPresente={load} />
            ))}
          </div>
        )}
      </section>

      <section className="as-panel">
        <div className="as-section-title"><h3>Mi asistencia por curso</h3></div>
        <div className="as-table-wrap">
          <table className="as-table">
            <thead>
              <tr>
                <th>Curso</th>
                <th>Año/división</th>
                <th className="num">Clases</th>
                <th className="num">Presentes</th>
                <th className="num">Tarde</th>
                <th className="num">Ausentes</th>
                <th className="num">Justificadas</th>
                <th className="num">Asistencia</th>
              </tr>
            </thead>
            <tbody>
              {data.resumenPorCurso.map((curso) => {
                const porcentaje = v(curso, 'porcentajeAsistencia', 'PorcentajeAsistencia');
                return (
                  <tr key={v(curso, 'idCurso', 'IdCurso')}>
                    <td><strong>{v(curso, 'nombreCurso', 'NombreCurso')}</strong></td>
                    <td>{v(curso, 'divisionNombre', 'DivisionNombre')}</td>
                    <td className="num">{v(curso, 'total', 'Total')}</td>
                    <td className="num">{v(curso, 'presentes', 'Presentes')}</td>
                    <td className="num">{v(curso, 'tardes', 'Tardes')}</td>
                    <td className="num">{v(curso, 'ausentes', 'Ausentes')}</td>
                    <td className="num">{v(curso, 'justificados', 'Justificados')}</td>
                    <td className={`num as-porcentaje${porcentaje != null && porcentaje < 75 ? ' low' : ''}`}>{porcentaje == null ? '—' : `${String(porcentaje).replace('.', ',')}%`}</td>
                  </tr>
                );
              })}
              {!loading && data.resumenPorCurso.length === 0 && (
                <tr><td colSpan={8} className="as-empty">Todavía no tenés cursos con asistencia.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="as-panel">
        <div className="as-section-title"><h3>Historial</h3></div>
        <div className="as-table-wrap">
          <table className="as-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Año/división</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {data.historial.map((asistencia) => {
                const info = getEstadoAsistencia(asistencia.estado);
                return (
                  <tr key={asistencia.id}>
                    <td>{formatFechaHoraUtc(asistencia.inicioUtc)}</td>
                    <td>{asistencia.divisionNombre}{asistencia.titulo ? ` · ${asistencia.titulo}` : ''}</td>
                    <td><span className={`as-chip as-chip-${asistencia.abierta && asistencia.estado === 'pendiente' ? 'live' : info.tone}`}>{asistencia.abierta && asistencia.estado === 'pendiente' ? 'Abierta' : info.label}</span></td>
                  </tr>
                );
              })}
              {!loading && data.historial.length === 0 && (
                <tr><td colSpan={3} className="as-empty">Todavía no hay asistencias.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AlumnoAsistencias;
